import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ErrorState } from '../state'
import DocumentLink from '../documents/DocumentLink'
import { recordHref, type Catalogue, type ClientView, type FundView } from './context'
import { useRead } from './useRead'
import SearchResults from './SearchResults'
import FolderDocuments from './FolderDocuments'
import './navigation.css'

function FundRow({ fund, clientId }: { fund: FundView; clientId?: string }) {
  const context = clientId ? '?client=' + encodeURIComponent(clientId) : ''
  return (
    <div className="nav-fund-row" data-fund-id={fund.id}>
      <div>
        <Link to={`/v2/funds/${fund.id}${context}`}>
          <strong>{fund.name}</strong>
        </Link>
        <small>{[fund.legal_form, fund.jurisdiction].filter(Boolean).join(' · ')}</small>
      </div>
      <span className="nav-muted">
        {fund.queues.book.card_count} Book · {fund.queues.evidence.card_count} Evidence ·{' '}
        {fund.queues.chase.card_count} Chase
      </span>
      <Link className="nav-link" to={`/v2/funds/${fund.id}${context}`}>
        Open overview ›
      </Link>
    </div>
  )
}
function ClientSection({
  client,
  funds,
  expanded,
  onToggle,
  onFolder,
}: {
  client: ClientView
  funds: FundView[]
  expanded: boolean
  onToggle: () => void
  onFolder: (folder?: ClientView['fund_folders'][number]) => void
}) {
  const uniqueIds = new Set(
    client.fund_folders
      .filter((folder) => folder.match_status === 'unique')
      .flatMap((folder) => folder.matching_registered_funds.map((fund) => fund.id))
  )
  return (
    <section className="nav-source" data-client-id={client.id}>
      <button className="nav-source-head" aria-expanded={expanded} onClick={onToggle}>
        <span className="nav-folder" aria-hidden="true">
          ▱
        </span>
        <span>
          <strong>{client.display_name ?? 'Client name not recorded'}</strong>
          <small>
            {client.source.name} · {client.folder_reference}
          </small>
        </span>
        <span className="count">{uniqueIds.size} related funds</span>
        <span className="chevron">›</span>
      </button>
      {expanded ? (
        <div className="nav-fund-list">
          <div className="nav-context-links">
            <Link className="nav-link" to={recordHref({ kind: 'client', client_id: client.id })}>
              Open client records ›
            </Link>
            <button className="nav-link" onClick={() => onFolder()}>
              Source documents ›
            </button>
          </div>
          {uniqueIds.size ? (
            <p className="nav-muted">Provisional folder matches · viewing context</p>
          ) : null}
          {funds
            .filter((fund) => uniqueIds.has(fund.id))
            .map((fund) => (
              <FundRow key={fund.id} fund={fund} clientId={client.id} />
            ))}
          {client.fund_folders
            .filter((folder) => folder.match_status !== 'unique')
            .map((folder) => (
              <div className="nav-fund-row" key={folder.id}>
                <div>
                  <strong>{folder.display_name ?? 'Fund name not recorded'}</strong>
                  <small>
                    {folder.match_status === 'ambiguous'
                      ? 'Several registered funds match this folder. Choose the intended viewing context.'
                      : 'No registered fund matched.'}
                  </small>
                  <button className="nav-link" onClick={() => onFolder(folder)}>
                    {folder.folder_reference} ›
                  </button>
                </div>
                <div className="nav-context-links">
                  {folder.matching_registered_funds.map((fund) => (
                    <Link
                      key={fund.id}
                      to={`/v2/funds/${fund.id}?client=${encodeURIComponent(client.id)}`}
                    >
                      {fund.name} ›
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          {!client.fund_folders.length ? <p>No fund folders recorded for this client.</p> : null}
        </div>
      ) : null}
    </section>
  )
}
export default function ContextFundsPage() {
  const [params, setParams] = useSearchParams()
  const rawPage = Number(params.get('page') ?? 1)
  const page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1
  const [query, setQuery] = useState('')
  const [retry, setRetry] = useState(0)
  const [folder, setFolder] = useState<{
    client: ClientView
    folder?: ClientView['fund_folders'][number]
  } | null>(null)
  const read = useRead<Catalogue>(`/api/navigation/catalogue?page=${page}`, retry)
  const value = read.value
  const discoveryIncomplete =
    value?.book_discovery_complete === false ||
    value?.funds.some((fund) => fund.book_discovery_complete === false) === true
  const expanded = params.get('client')
  const update = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
  }
  return (
    <main className="detail nav-funds" data-testid="context-funds">
      <header className="nav-search-head">
        <h1>Funds</h1>
        <p>Clients, funds and issuers</p>
      </header>
      <label className="nav-searchbox">
        <span aria-hidden="true">⌕</span>
        <input
          type="search"
          aria-label="Search clients, funds or issuers"
          placeholder="Search clients, funds or issuers"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      <div className="nav-search-section">
        <span>{query.trim() ? 'Matching names' : 'Clients'}</span>
        <Link className="nav-link" to={recordHref({ kind: 'all' })}>
          Open all records ›
        </Link>
      </div>
      {query.trim() ? (
        <SearchResults query={query} />
      ) : (
        <>
          {read.loading ? <p role="status">Loading clients and funds…</p> : null}
          {read.error ? (
            <ErrorState
              what="clients and funds"
              error={read.error}
              onRetry={() => setRetry((n) => n + 1)}
            />
          ) : null}
          {value ? (
            <>
              {discoveryIncomplete ? (
                <p role="status">
                  Some Book items could not be loaded. Book counts may be incomplete.
                </p>
              ) : null}
              <div className="nav-sources">
                {value.clients.map((client) => (
                  <ClientSection
                    key={client.id}
                    client={client}
                    funds={value.funds}
                    expanded={expanded === client.id}
                    onToggle={() => update('client', expanded === client.id ? null : client.id)}
                    onFolder={(selected) => setFolder({ client, folder: selected })}
                  />
                ))}
              </div>
              {value.client_total === 0 ? <p>No clients registered in intake yet.</p> : null}
              {page > 1 || value.client_has_more ? (
                <nav className="nav-paging" aria-label="Client pages">
                  <button disabled={page === 1} onClick={() => update('page', String(page - 1))}>
                    Previous
                  </button>
                  <span>
                    Page {page} · {value.client_total} clients
                  </span>
                  <button
                    disabled={!value.client_has_more}
                    onClick={() => update('page', String(page + 1))}
                  >
                    Next
                  </button>
                </nav>
              ) : null}
              {value.unlinked_fund_ids.length ? (
                <section className="nav-unlinked">
                  <h2>Registered funds without a unique client match</h2>
                  <p className="nav-muted">
                    Available independently of the intake folder grouping.
                  </p>
                  {value.funds
                    .filter((fund) => value.unlinked_fund_ids.includes(fund.id))
                    .map((fund) => (
                      <FundRow key={fund.id} fund={fund} />
                    ))}
                </section>
              ) : null}
              {value.unassigned_work.length ? (
                <details className="nav-unlinked">
                  <summary>{value.unassigned_work.length} items need context</summary>
                  <p>These items have no confirmed fund context in this view.</p>
                  {value.unassigned_documents.map((doc) => (
                    <div key={doc.document_id}>
                      <DocumentLink document={doc} />
                    </div>
                  ))}
                  <Link className="nav-link" to="/v2/traces">
                    Open Documents ›
                  </Link>
                </details>
              ) : null}
            </>
          ) : null}
        </>
      )}
      {folder ? (
        <FolderDocuments
          key={folder.client.id + ':' + (folder.folder?.id ?? '')}
          {...folder}
          onClose={() => setFolder(null)}
        />
      ) : null}
    </main>
  )
}
